import { useCallback, useMemo } from "react";
import { useApiContext, API_BASE, handleResponse } from "@/contexts/ApiContext";
import type {
  ShareResult,
  CollaboratorList,
  InviteResult,
  PendingInvite,
  UserCollaborator,
} from "@/lib/api";

// Iter 8 — enable() takes an optional extras bag so callers can
// set permission / expiry in the same round-trip. Existing callers
// (mainly the section component) keep working because every extra
// is optional.
export interface EnableOptions {
  permission?: "view" | "comment";
  expiresAt?: string | null;
  clearExpiry?: boolean;
}

export interface UseSharingApiReturn {
  enable: (documentId: string, isPublic: boolean, options?: EnableOptions) => Promise<ShareResult>;
  revoke: (documentId: string) => Promise<void>;
  listCollaborators: (documentId: string) => Promise<CollaboratorList>;
  invite: (documentId: string, data: { email: string; role: string }) => Promise<InviteResult>;
  /** Iter 10 — direct add by userId (skips the email-lookup path).
   *  Used by the team-member quickpick where we already know the
   *  target user (it's a team member, looked up by useTeamMembers). */
  addUserById: (documentId: string, userId: string, role: string) => Promise<UserCollaborator>;
  updateRole: (documentId: string, userId: string, role: string) => Promise<UserCollaborator>;
  remove: (documentId: string, userId: string) => Promise<void>;
  /** Undo a removal: back with their role, without a notification or email. */
  restore: (documentId: string, userId: string, role: string) => Promise<UserCollaborator>;
  /** After the Undo window, tell the removed person (the owner chose to). */
  sendRemovalNotice: (documentId: string, userId: string, allowReply: boolean) => Promise<void>;
  /** Invitations not yet accepted, pending or expired, oldest first. */
  listInvites: (documentId: string) => Promise<PendingInvite[]>;
  /** Send an invitation again (same link), valid 14 more days. */
  resendInvite: (documentId: string, inviteId: string) => Promise<InviteResult>;
  /** Withdraw an invitation; its link stops working. */
  cancelInvite: (documentId: string, inviteId: string) => Promise<void>;
}

export function useSharingApi(): UseSharingApiReturn {
  const { fetchWithAuth } = useApiContext();

  const enable = useCallback(
    async (documentId: string, isPublic: boolean, options?: EnableOptions) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/share`, {
        method: "POST",
        body: JSON.stringify({
          isPublic,
          ...(options?.permission ? { permission: options.permission } : {}),
          ...(options?.expiresAt !== undefined ? { expiresAt: options.expiresAt } : {}),
          ...(options?.clearExpiry ? { clearExpiry: true } : {}),
        }),
      });
      return handleResponse<ShareResult>(response);
    },
    [fetchWithAuth]
  );

  const revoke = useCallback(
    async (documentId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/share`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to revoke share link");
      }
    },
    [fetchWithAuth]
  );

  const listCollaborators = useCallback(
    async (documentId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators`);
      return handleResponse<CollaboratorList>(response);
    },
    [fetchWithAuth]
  );

  const invite = useCallback(
    async (documentId: string, data: { email: string; role: string }) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators/invite`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      return handleResponse<InviteResult>(response);
    },
    [fetchWithAuth]
  );

  const addUserById = useCallback(
    async (documentId: string, userId: string, role: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators/users`, {
        method: "POST",
        body: JSON.stringify({ userId, role }),
      });
      return handleResponse<UserCollaborator>(response);
    },
    [fetchWithAuth]
  );

  const updateRole = useCallback(
    async (documentId: string, userId: string, role: string) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/collaborators/users/${userId}`,
        {
          method: "PUT",
          body: JSON.stringify({ role }),
        }
      );
      return handleResponse<UserCollaborator>(response);
    },
    [fetchWithAuth]
  );

  const remove = useCallback(
    async (documentId: string, userId: string) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/collaborators/users/${userId}`,
        { method: "DELETE" }
      );
      if (!response.ok) {
        throw new Error("Failed to remove collaborator");
      }
    },
    [fetchWithAuth]
  );

  const restore = useCallback(
    async (documentId: string, userId: string, role: string) => {
      const response = await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/collaborators/users/${encodeURIComponent(userId)}/restore`,
        { method: "POST", body: JSON.stringify({ role }) }
      );
      return handleResponse<UserCollaborator>(response);
    },
    [fetchWithAuth]
  );

  const sendRemovalNotice = useCallback(
    async (documentId: string, userId: string, allowReply: boolean) => {
      // 409: they have access again (undone elsewhere): nothing to tell.
      await fetchWithAuth(
        `${API_BASE}/documents/${documentId}/collaborators/users/${encodeURIComponent(userId)}/removal-notice${allowReply ? "?allowReply=true" : ""}`,
        { method: "POST" }
      );
    },
    [fetchWithAuth]
  );

  const listInvites = useCallback(
    async (documentId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators/invites`);
      return handleResponse<PendingInvite[]>(response);
    },
    [fetchWithAuth]
  );

  const resendInvite = useCallback(
    async (documentId: string, inviteId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators/invites/${inviteId}/resend`, {
        method: "POST",
      });
      return handleResponse<InviteResult>(response);
    },
    [fetchWithAuth]
  );

  const cancelInvite = useCallback(
    async (documentId: string, inviteId: string) => {
      const response = await fetchWithAuth(`${API_BASE}/documents/${documentId}/collaborators/invites/${inviteId}`, {
        method: "DELETE",
      });
      if (!response.ok) {
        throw new Error("Failed to cancel the invitation");
      }
    },
    [fetchWithAuth]
  );

  // Memoize the return object — without this every call returns a
  // fresh object literal whose identity changes each render, so any
  // consumer that depends on `sharingApi` (e.g. inside a useCallback
  // or useEffect dep array) infinite-loops. The individual callbacks
  // are already stable above; the wrapper just had to follow suit.
  return useMemo(
    () => ({ enable, revoke, listCollaborators, invite, addUserById, updateRole, remove, restore, sendRemovalNotice, listInvites, resendInvite, cancelInvite }),
    [enable, revoke, listCollaborators, invite, addUserById, updateRole, remove, restore, sendRemovalNotice, listInvites, resendInvite, cancelInvite],
  );
}
